import { toast } from 'sonner';

// Success notifications
export const showSuccess = (message, options = {}) => {
  return toast.success(message, {
    duration: 4000,
    position: 'top-right',
    ...options,
  });
};

// Error notifications
export const showError = (message, options = {}) => {
  return toast.error(message, {
    duration: 6000,
    position: 'top-right',
    ...options,
  });
};

// Warning notifications
export const showWarning = (message, options = {}) => {
  return toast.warning(message, {
    duration: 5000,
    position: 'top-right',
    ...options,
  });
};

// Info notifications
export const showInfo = (message, options = {}) => {
  return toast.info(message, {
    duration: 4000,
    position: 'top-right',
    ...options,
  });
};

// Loading notifications
export const showLoading = (message, options = {}) => {
  return toast.loading(message, {
    position: 'top-right',
    ...options,
  });
};

// Dismiss specific toast
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

// Dismiss all toasts
export const dismissAllToasts = () => {
  toast.dismiss();
};

// API Error Handler
export const handleApiError = (error, customMessage = null) => {
  let message = customMessage;

  if (!message) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 400:
          message = data?.message || 'Invalid request. Please check your input.';
          break;
        case 401:
          message = 'Authentication required. Please log in again.';
          break;
        case 403:
          message = 'Access denied. You don\'t have permission for this action.';
          break;
        case 404:
          message = 'The requested resource was not found.';
          break;
        case 422:
          message = data?.message || 'Validation error. Please check your input.';
          break;
        case 429:
          message = 'Too many requests. Please try again later.';
          break;
        case 500:
          message = 'Server error. Please try again later.';
          break;
        case 502:
        case 503:
        case 504:
          message = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          message = data?.message || 'An unexpected error occurred.';
      }
    } else if (error.request) {
      // Network error
      message = 'Network error. Please check your connection and try again.';
    } else {
      // Other error
      message = error.message || 'An unexpected error occurred.';
    }
  }

  showError(message);
  return message;
};

// API Success Handler
export const handleApiSuccess = (message, options = {}) => {
  return showSuccess(message, {
    description: 'Your action was completed successfully.',
    ...options,
  });
};

// Form validation error handler
export const handleValidationError = (errors) => {
  const errorMessages = Object.values(errors).filter(Boolean);
  if (errorMessages.length > 0) {
    showError(errorMessages[0]);
  }
};

// Payment specific handlers
export const handlePaymentError = (error) => {
  let message = 'Payment failed. Please try again.';
  
  if (error.code) {
    switch (error.code) {
      case 'PAYMENT_CANCELLED':
        message = 'Payment was cancelled.';
        break;
      case 'INSUFFICIENT_FUNDS':
        message = 'Insufficient funds. Please try a different payment method.';
        break;
      case 'CARD_DECLINED':
        message = 'Card was declined. Please try a different card.';
        break;
      case 'NETWORK_ERROR':
        message = 'Network error during payment. Please try again.';
        break;
      default:
        message = error.message || message;
    }
  }

  showError(message);
};

export const handlePaymentSuccess = () => {
  showSuccess('Payment successful!', {
    description: 'Your order has been placed successfully.',
  });
};

// Order specific handlers
export const handleOrderError = (error) => {
  let message = 'Failed to create order. Please try again.';
  
  if (error.response?.status === 422) {
    message = 'Please check your order details and try again.';
  } else if (error.response?.status === 400) {
    message = 'Invalid order data. Please check your cart.';
  }

  showError(message);
};

export const handleOrderSuccess = () => {
  showSuccess('Order created successfully!', {
    description: 'Redirecting to payment...',
  });
};
